import { useState, useEffect } from "react";
import { Handshake, Copy, Gift, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const money = (v) => `$${(v ?? 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Stat({ label, value, tone = "text-[#1A1A1A]", testid }) {
  return (
    <div className="min-w-0" data-testid={testid}>
      <p className={`text-lg sm:text-xl font-medium truncate ${tone}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-[#6E675F]">{label}</p>
    </div>
  );
}

function AffiliateCard({ a }) {
  const pts = a.commission_unit === "points";
  const fmt = (v) => (pts ? `${(v || 0).toLocaleString()} pts` : money(v || 0));
  const link = `${window.location.origin}/?ref=${encodeURIComponent(a.code)}`;
  const terms = pts ? `${a.commission_value} reward points per $1 of paid sales` : `${a.commission_value}% of paid sales`;
  const discount = a.discount_type === "percent" ? `${a.discount_value}% off` : `${money(a.discount_value)} off`;

  return (
    <div className="border border-[#DDD7CF] rounded-lg bg-white overflow-hidden" data-testid={`affiliate-earnings-${a.code}`}>
      <div className="px-5 py-4 flex items-center justify-between gap-3 flex-wrap border-b border-[#EFEBE6]">
        <div className="min-w-0">
          <p className="font-mono text-base font-bold text-[#1A1A1A] tracking-wider">{a.code}</p>
          <p className="text-xs text-[#6E675F] mt-0.5">Your customers get {discount} · you earn {terms}</p>
        </div>
        <button
          data-testid={`affiliate-earnings-copy-${a.code}`}
          onClick={() => { navigator.clipboard.writeText(link); toast.success("Your affiliate link was copied"); }}
          className="inline-flex items-center gap-1.5 text-xs tracking-[0.1em] uppercase font-semibold text-white bg-[#8C7B6B] hover:bg-[#75665A] rounded-none px-4 py-2 transition-colors shrink-0"
        >
          <Copy className="w-3.5 h-3.5" /> Copy link
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 px-5 py-5">
        <Stat label="Paid sales" value={a.paid_orders} testid={`affiliate-earnings-sales-${a.code}`} />
        <Stat label="Sales volume" value={money(a.revenue)} />
        <Stat label="Lifetime earned" value={fmt(a.commission_earned)} testid={`affiliate-earnings-lifetime-${a.code}`} />
        <Stat label="Paid out" value={fmt(a.commission_settled)} testid={`affiliate-earnings-settled-${a.code}`} />
        <Stat label="Outstanding" value={fmt(a.commission_outstanding)} tone={a.commission_outstanding > 0 ? "text-[#2D6A4F]" : "text-[#9A9088]"} testid={`affiliate-earnings-outstanding-${a.code}`} />
      </div>
      {a.payouts.length > 0 && (
        <div className="px-5 pb-5" data-testid={`affiliate-earnings-payouts-${a.code}`}>
          <p className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#6E675F] mb-2">Payout history</p>
          <ul className="divide-y divide-[#EFEBE6] border border-[#EFEBE6] rounded-md">
            {a.payouts.map((p) => (
              <li key={p.id} className="flex items-center gap-3 px-3 py-2.5 text-sm" data-testid={`affiliate-payout-${p.id}`}>
                {p.method === "points" ? <Gift className="w-4 h-4 text-[#2D6A4F] shrink-0" /> : <DollarSign className="w-4 h-4 text-[#8C7B6B] shrink-0" />}
                <span className="font-medium text-[#1A1A1A]">{fmt(p.amount)}</span>
                <span className="text-xs text-[#6E675F]">
                  {p.method === "points" ? `${(p.points_credited || 0).toLocaleString()} reward points added to your balance` : "paid in dollars"}
                  {p.note ? ` · ${p.note}` : ""}
                </span>
                <span className="ml-auto text-xs text-[#6E675F] shrink-0">{new Date(p.created_at).toLocaleDateString("en-CA", { dateStyle: "medium" })}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export const AffiliateEarnings = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/affiliates/my").then(({ data: d }) => setData(d)).catch(() => setData({ affiliates: [] }));
  }, []);

  if (!data?.affiliates?.length) return null;

  return (
    <section className="mb-10" data-testid="affiliate-earnings">
      <div className="flex items-center gap-3 mb-4">
        <Handshake className="w-4 h-4 text-[#8C7B6B]" />
        <h2 className="font-display text-sm sm:text-base uppercase tracking-wide text-[#1A1A1A]">My Affiliate Earnings</h2>
        <div className="flex-1 h-px bg-[#DDD7CF]" />
      </div>
      <div className="space-y-4">
        {data.affiliates.map((a) => <AffiliateCard key={a.code} a={a} />)}
      </div>
      <p className="text-[11px] text-[#6E675F] mt-3">
        Commission accrues on verified, shipped and completed orders that used your code. Payouts in reward points are added to your balance immediately and can be applied at checkout.
      </p>
    </section>
  );
};
